/**
 * Hand-authored icon set for the Orthodox Way app shell.
 *
 * All icons share a 24×24 viewBox, stroke 1.5, currentColor, and the
 * round linecap/linejoin look. Pass `size` to scale; the stroke width
 * scales proportionally only when overridden explicitly.
 *
 * Visual coherence across the set matters more than per-icon precision,
 * keep paths simple, slightly irregular where natural, never naturalistic.
 */
import type { JSX, SVGProps } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  size?: number;
  strokeWidth?: number;
}

function base({ size = 24, strokeWidth = 1.5, className, ...rest }: IconProps) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true as const,
    className,
    ...rest,
  };
}

/* ──────────── Services ──────────── */

export function ChaliceIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="10.5" y1="3" x2="13.5" y2="3" />
      <path d="M6 7 H18 a0.5 0.5 0 0 1 0.5 0.5 V10 a6.5 6.5 0 0 1 -13 0 V7.5 a0.5 0.5 0 0 1 0.5 -0.5 Z" />
      <line x1="12" y1="16.5" x2="12" y2="20" />
      <line x1="8" y1="21" x2="16" y2="21" />
    </svg>
  );
}

export function CandleIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 2 Q13.5 4 13.5 5.5 Q13.5 7 12 7 Q10.5 7 10.5 5.5 Q10.5 4 12 2 Z" />
      <line x1="12" y1="7" x2="12" y2="9" />
      <rect x="9.5" y="9" width="5" height="13" rx="0.5" />
    </svg>
  );
}

export function SunriseIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M5 17 Q12 9 19 17" />
      <line x1="3" y1="20" x2="21" y2="20" />
      <line x1="12" y1="5" x2="12" y2="7" />
      <line x1="6.5" y1="7" x2="7.5" y2="8" />
      <line x1="17.5" y1="7" x2="16.5" y2="8" />
    </svg>
  );
}

export function MoonBookIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M17 6 A4 4 0 1 1 12 4" />
      <path d="M4 14 H12 a2 2 0 0 1 2 2 V21 a1 1 0 0 0 -1 -1 H4 Z" />
      <path d="M20 14 H12 a2 2 0 0 0 -2 2 V21 a1 1 0 0 1 1 -1 H20 Z" />
    </svg>
  );
}

export function MoonBookFullIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M3 9 Q12 1 21 9" />
      <path d="M4 14 H12 a2 2 0 0 1 2 2 V21 a1 1 0 0 0 -1 -1 H4 Z" />
      <path d="M20 14 H12 a2 2 0 0 0 -2 2 V21 a1 1 0 0 1 1 -1 H20 Z" />
    </svg>
  );
}

export function RoseIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 3 Q9 7 12 11 Q15 7 12 3 Z" />
      <path d="M6 9 Q6 14 12 12 Q9 9 6 9 Z" />
      <path d="M18 9 Q18 14 12 12 Q15 9 18 9 Z" />
      <line x1="12" y1="12" x2="12" y2="21" />
      <path d="M12 17 Q14 17 14 19" />
    </svg>
  );
}

export function CenserIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="11" y1="3" x2="13" y2="3" />
      <line x1="6" y1="6" x2="18" y2="6" />
      <line x1="7" y1="6" x2="10" y2="10" />
      <line x1="17" y1="6" x2="14" y2="10" />
      <line x1="12" y1="6" x2="12" y2="10" />
      <path d="M7 10 Q7 20 12 22 Q17 20 17 10 Z" />
      <path d="M9 16 Q12 18 15 16" />
    </svg>
  );
}

export function BlessingHandIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M9 21 V12 a1 1 0 0 1 2 0 V8 a1 1 0 0 1 2 0 V12" />
      <path d="M13 9 a1 1 0 0 1 2 0 V13" />
      <path d="M15 11 a1 1 0 0 1 1.5 0 V14" />
      <path d="M9 14 Q7 14 7 16 V19 a2 2 0 0 0 2 2" />
      <path d="M11 15 Q13 15 13 13" />
    </svg>
  );
}

/* ──────────── Sacraments ──────────── */

export function WaterDropIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 3 Q6 10 6 15 a6 6 0 0 0 12 0 Q18 10 12 3 Z" />
      <path d="M9 15 Q9 17 10.5 17.5" />
    </svg>
  );
}

export function OilDropIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 3 Q7 11 7 15 a5 5 0 0 0 10 0 Q17 11 12 3 Z" />
      <circle cx="10.5" cy="14" r="0.5" fill="currentColor" />
    </svg>
  );
}

export function WheatChaliceIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <line x1="12" y1="2" x2="12" y2="11" />
      <path d="M12 8 Q9 7 9 5 Q9 4 12 4" />
      <path d="M12 8 Q15 7 15 5 Q15 4 12 4" />
      <path d="M5 11 H19 a6.5 6.5 0 0 1 -13 0 V11 Z M5 11 H19" />
      <line x1="12" y1="17.5" x2="12" y2="20" />
      <line x1="8" y1="21" x2="16" y2="21" />
    </svg>
  );
}

export function BibleIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="5" y="3" width="14" height="18" rx="0.5" />
      <line x1="12" y1="7" x2="12" y2="13" />
      <line x1="9.5" y1="9.5" x2="14.5" y2="9.5" />
      <line x1="8" y1="16" x2="16" y2="16" />
    </svg>
  );
}

export function CrownPairIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M3 11 L4.5 8 L6 11 L7.5 8 L9 11 L10.5 8 L12 11 Z" />
      <path d="M12 11 L13.5 8 L15 11 L16.5 8 L18 11 L19.5 8 L21 11 Z" />
      <line x1="3" y1="13" x2="12" y2="13" />
      <line x1="12" y1="13" x2="21" y2="13" />
    </svg>
  );
}

export function LayingOnHandsIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="16" r="4" />
      <path d="M4 10 Q6 8 8 10 L11 13" />
      <path d="M20 10 Q18 8 16 10 L13 13" />
    </svg>
  );
}

/* ──────────── Saint categories ──────────── */

export function MartyrCrossIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <line x1="12" y1="3" x2="12" y2="21" />
      <line x1="7" y1="9" x2="17" y2="9" />
      <circle cx="12" cy="9" r="3.5" />
    </svg>
  );
}

export function MitreIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 3 Q8 6 7 11 Q7 14 9 16 H15 Q17 14 17 11 Q16 6 12 3 Z" />
      <line x1="12" y1="3" x2="12" y2="9" />
      <line x1="10" y1="6" x2="14" y2="6" />
      <line x1="7.5" y1="16" x2="16.5" y2="16" />
      <line x1="8.5" y1="19" x2="15.5" y2="19" />
    </svg>
  );
}

export function MonasticStaffIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M8 4 H16" />
      <path d="M12 4 V20" />
      <path d="M10 7 H14" />
    </svg>
  );
}

export function ScrollIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M5 6 H16 a3 3 0 0 1 3 3 V18 a3 3 0 0 1 -3 3 H5" />
      <path d="M5 6 a3 3 0 0 0 -3 3 V18 a3 3 0 0 0 3 3" />
      <line x1="9" y1="11" x2="15" y2="11" />
      <line x1="9" y1="15" x2="15" y2="15" />
    </svg>
  );
}

export function RighteousHandsIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M4 9 L10 15 Q12 17 14 15" />
      <path d="M20 9 L14 15 Q12 17 10 15" />
      <path d="M4 9 L6 7" />
      <path d="M20 9 L18 7" />
    </svg>
  );
}

export function WonderworkerRaysIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="3" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="21" />
      <line x1="3" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="21" y2="12" />
      <line x1="5.5" y1="5.5" x2="6.7" y2="6.7" />
      <line x1="17.3" y1="17.3" x2="18.5" y2="18.5" />
      <line x1="5.5" y1="18.5" x2="6.7" y2="17.3" />
      <line x1="17.3" y1="6.7" x2="18.5" y2="5.5" />
    </svg>
  );
}

export function TheotokosIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M5 8 Q12 2 19 8" />
      <rect x="7" y="8" width="10" height="13" rx="0.5" />
      <path d="M10 13 V18 M14 13 V18 M10 13 L14 18 M14 13 L10 18" />
    </svg>
  );
}

export function MotherOfGodIconIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M4 7 Q12 1 20 7" />
      <rect x="6" y="7" width="12" height="14" rx="0.5" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M12 14.5 V18 M9.5 17 H14.5" />
    </svg>
  );
}

export function EquaApostlesIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M6 7 H15 a3 3 0 0 1 3 3 V17 a3 3 0 0 1 -3 3 H6" />
      <path d="M6 7 a3 3 0 0 0 -3 3 V17 a3 3 0 0 0 3 3" />
      <line x1="12" y1="10" x2="12" y2="17" />
      <line x1="9.5" y1="13" x2="14.5" y2="13" />
    </svg>
  );
}

export function FoolForChristIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M9 17 Q7 15 7 12 Q7 9 10 9 Q12 9 12 11 V17 Q12 19 10 19 Q9 19 9 17 Z" />
      <circle cx="14" cy="7" r="0.7" fill="currentColor" />
      <circle cx="16" cy="9" r="0.6" fill="currentColor" />
      <circle cx="15" cy="11" r="0.5" fill="currentColor" />
      <circle cx="17" cy="6" r="0.5" fill="currentColor" />
    </svg>
  );
}

export function GeneralSaintIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="12" r="6" />
    </svg>
  );
}

/* ──────────── Calendar / liturgical ──────────── */

export function FastBadgeIcon({
  level = 'relaxed',
  ...props
}: IconProps & { level?: 'strict' | 'relaxed' | 'free' }) {
  const fills = {
    strict: [true, true, true],
    relaxed: [true, true, false],
    free: [false, false, false],
  } as const;
  const filled = fills[level];
  const drop = (cx: number, on: boolean, key: string) => (
    <path
      key={key}
      d={`M${cx} 6 Q${cx - 2.5} 11 ${cx - 2.5} 14 a2.5 2.5 0 0 0 5 0 Q${cx + 2.5} 11 ${cx} 6 Z`}
      fill={on ? 'currentColor' : 'none'}
    />
  );
  return (
    <svg {...base(props)}>
      {drop(6, filled[0], 'a')}
      {drop(12, filled[1], 'b')}
      {drop(18, filled[2], 'c')}
    </svg>
  );
}

export function FeastDotIcon({ size = 8, className, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 8 8"
      fill="currentColor"
      aria-hidden
      className={className}
      {...rest}
    >
      <circle cx="4" cy="4" r="3" />
    </svg>
  );
}

export function CalendarOldIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="13" cy="12" r="6" />
      <path d="M8 10 L5 12 L8 14" />
    </svg>
  );
}

export function CalendarNewIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M7 6 V18 M7 6 L11 12 V6 M11 12 V18" />
      <path d="M16 10 L19 12 L16 14" />
    </svg>
  );
}

export function CalendarMonthIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="4" y="6" width="16" height="14" rx="1.5" />
      <path d="M4 10 H20" />
      <path d="M9 4 V8 M15 4 V8" />
      <circle cx="8.5" cy="13.5" r="0.7" />
      <circle cx="12" cy="13.5" r="0.7" />
      <circle cx="15.5" cy="13.5" r="0.7" />
      <circle cx="8.5" cy="16.5" r="0.7" />
      <circle cx="12" cy="16.5" r="0.7" />
    </svg>
  );
}

/* ──────────── UI utility ──────────── */

export function ChevronLeftIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M15 5 L8 12 L15 19" />
    </svg>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M9 5 L16 12 L9 19" />
    </svg>
  );
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M5 9 L12 16 L19 9" />
    </svg>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="11" cy="11" r="6" />
      <line x1="15.5" y1="15.5" x2="20" y2="20" />
    </svg>
  );
}

export function XIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M5 12 L10 17 L19 7" />
    </svg>
  );
}

export function ArrowRightIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <line x1="4" y1="12" x2="20" y2="12" />
      <path d="M15 7 L20 12 L15 17" />
    </svg>
  );
}

export function ArrowUpIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <line x1="12" y1="20" x2="12" y2="4" />
      <path d="M7 9 L12 4 L17 9" />
    </svg>
  );
}

export function MenuIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

export function MenuCloseIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

export function SunIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="3" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="21" />
      <line x1="3" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="21" y2="12" />
      <line x1="5.5" y1="5.5" x2="6.7" y2="6.7" />
      <line x1="17.3" y1="17.3" x2="18.5" y2="18.5" />
      <line x1="5.5" y1="18.5" x2="6.7" y2="17.3" />
      <line x1="17.3" y1="6.7" x2="18.5" y2="5.5" />
    </svg>
  );
}

export function MoonIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M19 14 A8 8 0 1 1 10 5 A6 6 0 0 0 19 14 Z" />
    </svg>
  );
}

/* ──────────── Saint-category icon resolver ──────────── */

export type SaintCategory =
  | 'martyr'
  | 'hierarch'
  | 'monastic'
  | 'apostle'
  | 'righteous'
  | 'wonderworker'
  | 'theotokos'
  | 'icon-mother-of-god'
  | 'equal-apostles'
  | 'fool-for-christ'
  | 'other';

const CATEGORY_ICON: Record<SaintCategory, (p: IconProps) => JSX.Element> = {
  martyr: MartyrCrossIcon,
  hierarch: MitreIcon,
  monastic: MonasticStaffIcon,
  apostle: ScrollIcon,
  righteous: RighteousHandsIcon,
  wonderworker: WonderworkerRaysIcon,
  theotokos: TheotokosIcon,
  'icon-mother-of-god': MotherOfGodIconIcon,
  'equal-apostles': EquaApostlesIcon,
  'fool-for-christ': FoolForChristIcon,
  other: GeneralSaintIcon,
};

export function SaintCategoryIcon({ category, ...rest }: IconProps & { category: SaintCategory }) {
  const Icon = CATEGORY_ICON[category] ?? GeneralSaintIcon;
  return <Icon {...rest} />;
}
